import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banknote,
  CalendarDays,
  ClipboardCheck,
  FileBarChart,
  Home,
  PieChart,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useHR } from '../hooks/useHR';
import AttendanceManagement from './hr/AttendanceManagement';
import EmployeesManagement from './hr/EmployeesManagement';
import LeavesManagement from './hr/LeavesManagement';
import PayrollManagement from './hr/PayrollManagement';
import { AppShell } from '../components/layout';
import MetricCard from '../components/ui/MetricCard';
import Surface from '../components/ui/Surface';
import type { NavigationItem } from '../types/ui';

type HRTab = 'employees' | 'attendance' | 'leaves' | 'payroll' | 'reports';

const tabs: Array<{ id: HRTab; label: string; icon: typeof Users }> = [
  { id: 'employees', label: 'الموظفون', icon: Users },
  { id: 'attendance', label: 'الحضور', icon: ClipboardCheck },
  { id: 'leaves', label: 'الإجازات', icon: CalendarDays },
  { id: 'payroll', label: 'الرواتب', icon: Banknote },
  { id: 'reports', label: 'التقارير', icon: FileBarChart },
];

const pageDescriptions: Record<HRTab, string> = {
  employees: 'إدارة الحسابات الوظيفية والرواتب والحالة التشغيلية من بيانات النظام الفعلية.',
  attendance: 'متابعة حضور الموظفين وتسجيل الانصراف والتأخير حسب الفترة المحددة.',
  leaves: 'مراجعة طلبات الإجازات واعتمادها أو رفضها مع ربطها بسجلات الموظفين.',
  payroll: 'حساب ومراجعة واعتماد وصرف كشوف الرواتب بالجنيه المصري.',
  reports: 'ملخص تشغيلي للحضور والإجازات والرواتب من بيانات المؤسسة الحالية.',
};

const HRPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<HRTab>('employees');
  const { employees, attendance, leaves, payroll } = useHR();
  const employeeList = employees.employees;
  const {
    fetchOrganizationAttendance,
    organizationAttendance,
  } = attendance;
  const {
    fetchOrganizationLeaves,
    organizationLeaves,
  } = leaves;
  const { fetchOrganizationPayrolls, organizationPayrolls } = payroll;

  const dashboardHref =
    user?.role === 'employee' ? '/employee/dashboard' : '/dashboard';

  const navigation = useMemo<NavigationItem[]>(
    () => [
      {
        id: 'dashboard',
        label: 'لوحة التحكم',
        icon: Home,
        group: 'عام',
        href: dashboardHref,
      },
      ...tabs.map(tab => ({
        id: tab.id,
        label: tab.label,
        icon: tab.icon,
        group: 'الموارد البشرية',
        mobile: tab.id === 'employees' || tab.id === 'attendance',
      })),
    ],
    [dashboardHref]
  );

  const activeItem = navigation.find(item => item.id === activeTab);

  useEffect(() => {
    if (activeTab !== 'reports') return;

    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    if (employeeList.length) {
      fetchOrganizationAttendance(employeeList, startOfMonth, endOfMonth);
    }
    fetchOrganizationLeaves();
    fetchOrganizationPayrolls();
  }, [
    activeTab,
    employeeList,
    fetchOrganizationAttendance,
    fetchOrganizationLeaves,
    fetchOrganizationPayrolls,
  ]);

  const reportStats = useMemo(() => {
    const employeesForReports = employeeList ?? [];
    const attendanceRecords = organizationAttendance ?? [];
    const leaveRecords = organizationLeaves ?? [];
    const payrollRecords = organizationPayrolls ?? [];
    const activeCount = employeesForReports.filter(
      employee => employee.isActive
    ).length;
    const presentDays = attendanceRecords.filter(record =>
      ['present', 'late', 'half_day'].includes(record.status)
    ).length;
    const absentDays = attendanceRecords.filter(
      record => record.status === 'absent'
    ).length;
    const lateDays = attendanceRecords.filter(
      record => record.status === 'late'
    ).length;
    const totalHours = attendanceRecords.reduce(
      (sum, record) => sum + (record.totalHours || 0),
      0
    );
    const approvedLeaves = leaveRecords.filter(
      leave => leave.status === 'approved'
    );
    const pendingLeaveRecords = leaveRecords.filter(
      leave => leave.status === 'pending'
    );
    const payrollTotal = payrollRecords.reduce(
      (sum, item) => sum + (item.netSalary || 0),
      0
    );
    const paidPayrollTotal = payrollRecords
      .filter(item => item.status === 'paid')
      .reduce((sum, item) => sum + (item.netSalary || 0), 0);

    return {
      activeCount,
      attendanceRecords: attendanceRecords.length,
      presentDays,
      absentDays,
      lateDays,
      totalHours,
      approvedLeaveDays: approvedLeaves.reduce(
        (sum, leave) => sum + (leave.totalDays || 0),
        0
      ),
      pendingLeaves: pendingLeaveRecords.length,
      payrollTotal,
      paidPayrollTotal,
      payrollsCount: payrollRecords.length,
    };
  }, [
    employeeList,
    organizationAttendance,
    organizationLeaves,
    organizationPayrolls,
  ]);

  const renderReports = () => {
    const attendanceRate = reportStats.attendanceRecords
      ? Math.round(
          (reportStats.presentDays / reportStats.attendanceRecords) * 100
        )
      : 0;
    const payrollPaidRate = reportStats.payrollTotal
      ? Math.round(
          (reportStats.paidPayrollTotal / reportStats.payrollTotal) * 100
        )
      : 0;

    return (
      <div className='space-y-6'>
        <section className='grid gap-4 md:grid-cols-2 xl:grid-cols-4'>
          <MetricCard
            label='نسبة الحضور'
            value={`${attendanceRate}%`}
            icon={TrendingUp}
            tone={attendanceRate >= 85 ? 'green' : 'amber'}
            note={`${reportStats.presentDays} يوم حضور من ${reportStats.attendanceRecords}`}
          />
          <MetricCard
            label='التأخير'
            value={reportStats.lateDays}
            icon={ClipboardCheck}
            tone={reportStats.lateDays ? 'amber' : 'green'}
            note='عدد أيام التأخير المسجلة هذا الشهر'
          />
          <MetricCard
            label='أيام الإجازات المعتمدة'
            value={reportStats.approvedLeaveDays}
            icon={CalendarDays}
            tone='blue'
            note={`${reportStats.pendingLeaves} طلبات إجازة معلقة`}
          />
          <MetricCard
            label='صرف الرواتب'
            value={`${payrollPaidRate}%`}
            icon={Banknote}
            tone={payrollPaidRate >= 90 ? 'green' : 'amber'}
            note={`${reportStats.payrollsCount} كشوف في الدورة الحالية`}
          />
        </section>

        <div className='grid gap-4 lg:grid-cols-2'>
          <Surface>
            <div className='mb-4 flex items-center gap-2'>
              <PieChart className='h-5 w-5 text-primary-600' />
              <h3 className='text-lg font-semibold text-secondary-900 dark:text-secondary-50'>
                ملخص الحضور الشهري
              </h3>
            </div>
            <dl className='grid grid-cols-2 gap-4 text-sm'>
              <ReportValue label='سجلات الحضور' value={reportStats.attendanceRecords} />
              <ReportValue
                label='إجمالي ساعات العمل'
                value={reportStats.totalHours.toFixed(1)}
              />
              <ReportValue
                label='أيام الغياب'
                value={reportStats.absentDays}
                className='text-red-600'
              />
              <ReportValue label='موظفون نشطون' value={reportStats.activeCount} />
            </dl>
          </Surface>

          <Surface>
            <div className='mb-4 flex items-center gap-2'>
              <FileBarChart className='h-5 w-5 text-primary-600' />
              <h3 className='text-lg font-semibold text-secondary-900 dark:text-secondary-50'>
                ملخص الرواتب
              </h3>
            </div>
            <dl className='grid grid-cols-2 gap-4 text-sm'>
              <ReportValue
                label='إجمالي صافي الرواتب'
                value={`${reportStats.payrollTotal.toLocaleString('ar-EG')} EGP`}
              />
              <ReportValue
                label='المصروف فعليًا'
                value={`${reportStats.paidPayrollTotal.toLocaleString('ar-EG')} EGP`}
                className='text-green-600'
              />
              <ReportValue label='كشوف الرواتب' value={reportStats.payrollsCount} />
              <ReportValue
                label='طلبات الإجازة المعلقة'
                value={reportStats.pendingLeaves}
                className='text-amber-600'
              />
            </dl>
          </Surface>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (activeTab === 'employees') return <EmployeesManagement />;
    if (activeTab === 'attendance') return <AttendanceManagement />;
    if (activeTab === 'leaves') return <LeavesManagement />;
    if (activeTab === 'payroll') return <PayrollManagement />;
    return renderReports();
  };

  return (
    <AppShell
      navigation={navigation}
      activeItemId={activeTab}
      onNavigate={(item: NavigationItem) => {
        if (item.id === 'dashboard') {
          navigate(dashboardHref);
          return;
        }
        setActiveTab(item.id as HRTab);
      }}
      userName={user?.name || user?.email || 'مستخدم'}
      userEmail={user?.email}
      userRoleLabel={
        user?.role === 'admin'
          ? 'مدير النظام'
          : user?.role === 'employee'
            ? 'موظف موارد بشرية'
            : 'مدرس'
      }
      pageTitle={activeItem?.label || 'الموارد البشرية'}
      pageDescription={pageDescriptions[activeTab]}
      onLogout={logout}
    >
      <div className='space-y-6'>
        {renderContent()}
      </div>
    </AppShell>
  );
};

const ReportValue: React.FC<{
  label: string;
  value: React.ReactNode;
  className?: string;
}> = ({ label, value, className = 'text-secondary-900 dark:text-secondary-50' }) => (
  <div>
    <dt className='text-secondary-500'>{label}</dt>
    <dd className={`mt-1 text-2xl font-bold ${className}`}>{value}</dd>
  </div>
);

export default HRPage;
